import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styledComponent from '../../utils/styledComponent';
import theme from '../../theme';
import Badge from '../../components/atoms/Badge';
import Button from '../../components/atoms/forms/Button';
import Input from '../../components/atoms/forms/Input';
import SingleSelect from '../../components/atoms/forms/SingleSelect';
import driverLabel from './driverLabel';

const UNASSIGNED_OPTION = { value: '', label: '— Unassigned —' };

const StyledTr = styledComponent('tr', {
  '& .rhr-inner': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.rowGap,
  },
  '& .rhr-left': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.innerGap,
  },
  '& .rhr-caret': {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: theme.sizes.bodyFontSize,
    padding: 0,
    color: theme.colors.textDark,
  },
  '& .rhr-caret:focus-visible': {
    outline: 'none',
    boxShadow: `0 0 0 2px ${theme.colors.infoBlue}33`,
  },
  '& .rhr-edit': {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing.innerGap,
  },
  '& .rhr-assign': {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.innerGap,
    fontSize: theme.sizes.smallFontSize,
    fontWeight: theme.fontWeights.medium,
  },
  '& .rhr-drivers-error': {
    color: theme.colors.danger,
    fontSize: theme.sizes.smallFontSize,
    fontWeight: theme.fontWeights.medium,
  },
});

function RouteHeaderRow({
  routeKey,
  displayName,
  routeColor,
  isAssignMode,
  isCollapsed,
  onToggleCollapse,
  drivers,
  driversError,
  driversLoading,
  selectedDriverEmail,
  onDriverChange,
  onNameSave,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [draftName, setDraftName] = useState('');

  const shownName = displayName || routeKey;

  const startEditing = () => {
    setDraftName(shownName);
    setIsEditing(true);
  };
  const saveName = () => {
    onNameSave(draftName.trim());
    setIsEditing(false);
  };

  const options = [
    UNASSIGNED_OPTION,
    ...drivers.map((d) => ({
      value: d.emailAddress,
      label: driverLabel(d.emailAddress),
    })),
  ];
  if (
    selectedDriverEmail &&
    !drivers.some((d) => d.emailAddress === selectedDriverEmail)
  ) {
    // A persisted assignment can point at a driver no longer in the market's
    // list; keep it selectable so the dropdown never silently clears it.
    options.push({
      value: selectedDriverEmail,
      label: driverLabel(selectedDriverEmail),
    });
  }
  const selectedOption = selectedDriverEmail
    ? { value: selectedDriverEmail, label: driverLabel(selectedDriverEmail) }
    : UNASSIGNED_OPTION;

  return (
    <StyledTr className="header" style={{ background: routeColor }}>
      <td colSpan="9">
        <div className="rhr-inner">
          <div className="rhr-left">
            <button
              type="button"
              className="rhr-caret"
              aria-label={isCollapsed ? 'Expand route' : 'Collapse route'}
              onClick={onToggleCollapse}
            >
              {isCollapsed ? '▸' : '▾'}
            </button>
            {isEditing ? (
              <span className="rhr-edit">
                <span>ROUTE:</span>
                <Input
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  aria-label="Route name"
                />
                <Button
                  variant="link"
                  title="Save name"
                  disabled={!draftName.trim()}
                  onClick={saveName}
                  dataTestId="rhr-rename-save"
                >
                  save
                </Button>
                <Button
                  variant="link"
                  title="Cancel"
                  onClick={() => setIsEditing(false)}
                  dataTestId="rhr-rename-cancel"
                >
                  cancel
                </Button>
              </span>
            ) : (
              <span className="rhr-edit">
                <span>ROUTE: {shownName}</span>
                {isAssignMode && (
                  <Button
                    variant="link"
                    title="Rename route"
                    onClick={startEditing}
                    dataTestId="rhr-rename"
                  >
                    ✎ Rename
                  </Button>
                )}
              </span>
            )}
          </div>
          {isAssignMode && (
            <span className="rhr-assign">
              {driversError ? (
                <span className="rhr-drivers-error">Couldn’t load drivers</span>
              ) : (
                <>
                  <span>Driver:</span>
                  <SingleSelect
                    options={options}
                    value={selectedOption}
                    onChange={(option) =>
                      onDriverChange(
                        option && option.value ? option.value : null,
                      )
                    }
                    width={theme.sizes.driverSelectWidth}
                    isLoading={driversLoading}
                  />
                </>
              )}
              {selectedDriverEmail ? (
                <Badge variant="success">Assigned</Badge>
              ) : (
                <Badge variant="attention">Needs driver</Badge>
              )}
            </span>
          )}
        </div>
      </td>
    </StyledTr>
  );
}

RouteHeaderRow.propTypes = {
  routeKey: PropTypes.string.isRequired,
  displayName: PropTypes.string,
  routeColor: PropTypes.string.isRequired,
  isAssignMode: PropTypes.bool,
  isCollapsed: PropTypes.bool,
  onToggleCollapse: PropTypes.func.isRequired,
  drivers: PropTypes.arrayOf(
    PropTypes.shape({ emailAddress: PropTypes.string.isRequired }),
  ),
  driversError: PropTypes.bool,
  driversLoading: PropTypes.bool,
  selectedDriverEmail: PropTypes.string,
  onDriverChange: PropTypes.func,
  onNameSave: PropTypes.func,
};

RouteHeaderRow.defaultProps = {
  displayName: null,
  isAssignMode: false,
  isCollapsed: false,
  drivers: [],
  driversError: false,
  driversLoading: false,
  selectedDriverEmail: null,
  onDriverChange: () => {},
  onNameSave: () => {},
};

export default RouteHeaderRow;
